import type { SupportedStateCode } from '@/common/constants/states';
import type { ContactsRequiredFields } from '../shared/schema';

export interface AdditionalContactOption {
  value: string;
  label: string;
}

export interface OfferContactsConfig {
  // Buyer side
  showBuyersAttorney: boolean;
  buyerAgentLicenseDisabled: boolean;
  showBuyerAgentMlsCode: boolean;
  showBuyerIntermediaryBanner: boolean;

  // Seller side
  showSellersAttorney: boolean;
  showSellerAgentMlsCode: boolean;
  showSellerIntermediaryBanner: boolean;
  /** GA drops the required validation on the seller's name. */
  sellerNameRequired: boolean;

  // Additional contacts
  showTitleOfficer: boolean;
  showEscrowOfficer: boolean;
  showAttorneySection: boolean;
  showClosingAttorneySection: boolean;
  showPropertyManagingAgentSection: boolean;
  // Final "add a contact" picker list; per-state configs fully replace it.
  additionalContactOptions: AdditionalContactOption[];
}

const BASE_ADDITIONAL_CONTACT_OPTIONS: AdditionalContactOption[] = [
  { value: 'additionalContacts.titleOfficer', label: 'Title Officer' },
  { value: 'additionalContacts.lender', label: 'Lender' },
  { value: 'additionalContacts.appraiser', label: 'Appraiser' },
  { value: 'additionalContacts.attorney', label: 'Attorney' },
  { value: 'additionalContacts.homeWarranty', label: 'Home Warranty' },
  { value: 'additionalContacts.other', label: 'Other' },
];

const DEFAULT_CONFIG: OfferContactsConfig = {
  showBuyersAttorney: false,
  buyerAgentLicenseDisabled: false,
  showBuyerAgentMlsCode: false,
  showBuyerIntermediaryBanner: false,

  showSellersAttorney: false,
  showSellerAgentMlsCode: false,
  showSellerIntermediaryBanner: false,
  sellerNameRequired: true,

  showTitleOfficer: true,
  showEscrowOfficer: false,
  showAttorneySection: true,
  showClosingAttorneySection: false,
  showPropertyManagingAgentSection: false,
  additionalContactOptions: BASE_ADDITIONAL_CONTACT_OPTIONS,
};

export type OfferContactsConfigDelta = Partial<OfferContactsConfig>;

/**
 * Per-state deltas merged onto `DEFAULT_CONFIG`. Top-level keys replace;
 * `additionalContactOptions` is a full replacement.
 */
const STATE_CONFIGS: Partial<
  Record<SupportedStateCode, OfferContactsConfigDelta>
> = {
  CA: {
    showEscrowOfficer: true,
  },
  GA: {
    showBuyerAgentMlsCode: true,
    showSellerAgentMlsCode: true,
    sellerNameRequired: false,
    showTitleOfficer: false,
    showAttorneySection: false,
    showClosingAttorneySection: true,
    additionalContactOptions: [
      {
        value: 'additionalContacts.closingAttorney',
        label: 'Closing Attorney',
      },
      { value: 'additionalContacts.lender', label: 'Lender' },
      { value: 'additionalContacts.appraiser', label: 'Appraiser' },
      { value: 'additionalContacts.homeWarranty', label: 'Home Warranty' },
      { value: 'additionalContacts.other', label: 'Other' },
    ],
  },
  NJ: {
    showSellersAttorney: true,
    additionalContactOptions: [
      { value: 'additionalContacts.appraiser', label: 'Appraiser' },
      { value: 'additionalContacts.homeWarranty', label: 'Home Warranty' },
      { value: 'additionalContacts.other', label: 'Other' },
    ],
  },
  NY: {
    showBuyersAttorney: true,
    showSellersAttorney: true,
    buyerAgentLicenseDisabled: true,
    showTitleOfficer: false,
    showAttorneySection: false,
    showPropertyManagingAgentSection: true,
    additionalContactOptions: [
      {
        value: 'additionalContacts.propertyManagingAgent',
        label: 'Property Managing Agent',
      },
      { value: 'additionalContacts.lender', label: 'Lender' },
      { value: 'additionalContacts.appraiser', label: 'Appraiser' },
      { value: 'additionalContacts.homeWarranty', label: 'Home Warranty' },
      { value: 'additionalContacts.other', label: 'Other' },
    ],
  },
  TX: {
    showBuyerIntermediaryBanner: true,
    showSellerIntermediaryBanner: true,
  },
};

/**
 * Build an `OfferContactsConfig` from a delta against `DEFAULT_CONFIG`.
 * Called with no argument returns the unmodified defaults.
 */
export const buildOfferContactsConfig = (
  delta: OfferContactsConfigDelta = {},
): OfferContactsConfig => ({
  ...DEFAULT_CONFIG,
  ...delta,
});

/** Resolve the per-state Offer contacts config. */
export const resolveOfferContactsConfig = (
  state: SupportedStateCode,
): OfferContactsConfig => buildOfferContactsConfig(STATE_CONFIGS[state]);

/**
 * The offer contacts' required name/email rules, derived from the resolved
 * config. Single source shared by the validation schema (sidebar badge +
 * lockdown) and the form's asterisk/highlight. The user represents the buyer,
 * so the buyer's own agent isn't required; the buyer (client) is; the seller's
 * agent needs a name; the seller's name is required except where the state
 * drops it (GA).
 */
export const getRequiredContactFields = (
  config: OfferContactsConfig,
): ContactsRequiredFields => ({
  agent: { firstName: false, lastName: false, email: false },
  counterpartyAgent: {
    firstName: true,
    lastName: true,
    email: false,
    licenseNumber: true,
    brokerageName: true,
    brokerageLicenseNumber: true,
  },
  client: { firstName: true, lastName: true, email: true },
  counterpartyClient: {
    firstName: config.sellerNameRequired,
    lastName: false,
    email: false,
  },
});
